import { updateMFAPreference, verifyTOTPSetup } from '@aws-amplify/auth';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useCallback, useState } from 'react';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';

const STEP_INDEX = 1;

interface Props {
  onComplete: () => void;
}

const VerifyStep: React.FC<Props> = ({ onComplete }) => {
  const { translation } = useTranslation();
  const { setFooterContent, steps } = useWizardStepContext();
  const { showSnackbar } = useSnackbarContext();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleVerify = useCallback(async () => {
    const fullCode = code.join('');
    if (fullCode.length < 6) return;
    setLoading(true);
    try {
      await verifyTOTPSetup({ code: fullCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      onComplete();
    } catch (err) {
      setError(true);
      showSnackbar(translation.error_enabling_2fa, { severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [code, onComplete, showSnackbar]);

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          id="verify-code-button"
          variant="contained"
          onClick={handleVerify}
          loading={loading}
          disabled={code.join('').length < 6}
        >
          {translation.verify}
        </Button>
      </Box>
    );
  }, [setFooterContent, steps, translation, handleVerify, loading, code]);

  return (
    <Stack gap={3} sx={{ p: 2 }}>
      <Typography variant="body2">{translation.authenticator_enter_code_hint}</Typography>
      <SixCodeTextField code={code} setCode={setCode} setError={setError} triggerWhenCodeIsFull={handleVerify} />
      {error && (
        <Alert severity="error">
          <Typography variant="subtitle2">{translation.invalid_verification_code}</Typography>
          <Typography variant="body2">{translation.verification_code_hint}</Typography>
        </Alert>
      )}
    </Stack>
  );
};

export default VerifyStep;
